import React from 'react';
import { motion } from 'framer-motion';
import { Code, Database, Cloud, Zap } from 'lucide-react';
import { FaCloud, FaBookOpen, FaProjectDiagram, FaCertificate, FaLightbulb, FaComments, FaUserTie, FaUsers } from "react-icons/fa";
import { skills } from '../data/mockData';
import { Progress } from './ui/progress';
import CountUp from 'react-countup';

// Skill categories with icons/colors
const skillCategories = {
  Frontend: { icon: Code, color: 'cyan' },
  Backend: { icon: Zap, color: 'blue' },
  Database: { icon: Database, color: 'purple' },
  Cloud: { icon: Cloud, color: 'green' },
  Tools: { icon: Code, color: 'orange' },
};

// Group technical skills
const groupedSkills = skills.reduce((acc, skill) => {
  if (!acc[skill.category]) acc[skill.category] = [];
  acc[skill.category].push(skill);
  return acc;
}, {});

// Stats grid
const statsData = [
  { number: 434, label: "Cloud Labs", suffix: "+", icon: <FaCloud /> },
  { number: 72, label: "Courses", suffix: "+", icon: <FaBookOpen /> },
  { number: 10, label: "Projects", suffix: "+", icon: <FaProjectDiagram /> },
  { number: 20, label: "Certificates", suffix: "+", icon: <FaCertificate /> },
];

// Professional/soft skills
const professionalSkills = [
  { name: "Professional Skills", level: 85, color: "cyan", icon: <FaUserTie className="mr-2" /> },
  { name: "Creativity", level: 80, color: "blue", icon: <FaLightbulb className="mr-2" /> },
  { name: "Communication", level: 75, color: "purple", icon: <FaComments className="mr-2" /> },
  { name: "Problem Solving", level: 80, color: "green", icon: <FaLightbulb className="mr-2" /> },
  { name: "Teamwork", level: 90, color: "orange", icon: <FaUsers className="mr-2" /> },
];

const About = () => (
  <motion.section
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1 }}
    id="about"
    className="py-20 bg-gray-900"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* About Heading */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
          About <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Me</span>
        </h2>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Passionate about building scalable applications and learning new technologies.
        </p>
      </motion.div>

      {/* Technical Skills Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {Object.entries(groupedSkills).map(([category, categorySkills], index) => {
          const { icon: Icon, color } = skillCategories[category] || { icon: Code, color: 'cyan' };
          return (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, boxShadow: '0 6px 14px rgba(6,182,212,0.10)' }}
              className="bg-gray-800/60 glass rounded-xl p-6 border border-gray-700 hover:border-cyan-400/50 shadow-xl transition-all"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className={`p-3 bg-${color}-500/20 rounded-lg`}>
                  <Icon size={24} className={`text-${color}-400`} />
                </div>
                <h3 className="text-xl font-semibold text-white">{category}</h3>
              </div>
              <div className="space-y-4">
                {categorySkills.map((skill, i) => (
                  <div key={skill.name}>
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-300 font-medium">{skill.name}</span>
                      <span className="text-cyan-400 text-sm">{skill.level}%</span>
                    </div>
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${skill.level}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.3 + i * 0.08 }}
                      style={{ overflow: "hidden" }}
                    >
                      <Progress value={skill.level} className="h-2" />
                    </motion.div>
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Professional Skills Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
        className="mt-16 mb-8 max-w-xl mx-auto bg-gray-800/60 glass rounded-xl p-8 shadow-2xl"
      >
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-7 text-center">
          Professional Skills
        </h3>
        <div className="space-y-6">
          {professionalSkills.map((skill, idx) => (
            <div key={skill.name}>
              <div className="flex justify-between items-center mb-2">
                <span className="flex items-center text-gray-300 font-medium">
                  {skill.icon}
                  {skill.name}
                </span>
                <span className={`text-${skill.color}-400 text-sm font-semibold`}>{skill.level}%</span>
              </div>
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${skill.level}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.18 + idx * 0.07 }}
                style={{ overflow: "hidden" }}
              >
                <Progress value={skill.level} className={`h-3 bg-${skill.color}-400/30`} />
              </motion.div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
        className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8"
      >
        {statsData.map((stat, idx) => (
          <motion.div
            key={stat.label}
            whileHover={{ y: -6, scale: 1.06, boxShadow: "0 8px 26px rgba(6,182,212,0.15)" }}
            className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 text-center border border-cyan-400/25 shadow-md transition-all duration-200 overflow-hidden"
          >
            <span
              className="absolute left-6 top-6 w-10 h-10 flex items-center justify-center rounded-full bg-cyan-500/20 text-cyan-400 text-2xl pointer-events-none"
            >
              {stat.icon}
            </span>
            <h4 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-2 drop-shadow-lg animate-pulse">
              <CountUp
                end={stat.number}
                duration={2}
                suffix={stat.suffix}
                enableScrollSpy
                scrollSpyDelay={100}
              />
            </h4>
            <p className="text-gray-400 font-semibold tracking-wide">{stat.label}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </motion.section>
);

export default About;
